import React, { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../api/admin';
import { RefreshCw, CreditCard, CheckCircle2, AlertCircle } from 'lucide-react';

const AdminPaypalDeposits = () => {
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [deposits, setDeposits] = useState([]);
  const [editableAmounts, setEditableAmounts] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  const statusParam = useMemo(() => (filter === 'pending' ? 0 : undefined), [filter]);

  useEffect(() => {
    loadDeposits(statusParam);
  }, [statusParam]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const loadDeposits = async (status) => {
    setLoading(true);
    try {
      const response = await adminApi.getPaypalDeposits(status);
      const rows = response?.paypalDeposits || [];
      setDeposits(rows);
      setEditableAmounts(
        rows.reduce((acc, row) => {
          acc[row.id] = Number(row.amount || 0);
          return acc;
        }, {})
      );
    } catch (error) {
      showMessage('error', error.message || 'Failed to load PayPal deposits');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (deposit) => {
    const amount = Number(editableAmounts[deposit.id]);
    if (!Number.isFinite(amount) || amount <= 0) {
      showMessage('error', 'Enter a valid amount before confirming');
      return;
    }

    if (!window.confirm(`Confirm PayPal deposit ${deposit.code} for ${amount.toFixed(2)} EUR / DIOS?`)) {
      return;
    }

    setConfirmingId(deposit.id);
    try {
      await adminApi.confirmPaypalDeposit(deposit.id, amount);
      showMessage('success', `PayPal deposit ${deposit.code} confirmed successfully`);
      await loadDeposits(statusParam);
    } catch (error) {
      showMessage('error', error.message || 'Failed to confirm PayPal deposit');
    } finally {
      setConfirmingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center">
            <CreditCard className="w-8 h-8 mr-3 text-sky-400" />
            PayPal Deposits
          </h1>
          <p className="text-sm text-gray-400 mt-2">
            Review pending PayPal deposits, adjust the credited amount if needed, then confirm to credit the player balance.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              filter === 'pending'
                ? 'bg-sky-600 border-sky-500 text-white'
                : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
            }`}
          >
            Pending
          </button>
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              filter === 'all'
                ? 'bg-sky-600 border-sky-500 text-white'
                : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
            }`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => loadDeposits(statusParam)}
            className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 hover:border-gray-500 inline-flex items-center"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`p-4 rounded-lg border ${
          message.type === 'success'
            ? 'bg-green-900/40 border-green-700 text-green-300'
            : 'bg-red-900/40 border-red-700 text-red-300'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 mr-2" />
            )}
            {message.text}
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading PayPal deposits...</div>
        ) : deposits.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No PayPal deposits found for this filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-950/70 text-gray-300">
                <tr>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-left">Code</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Amount</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {deposits.map((deposit) => (
                  <tr key={deposit.id} className="border-t border-gray-800">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{deposit.username || `User ${deposit.userId}`}</div>
                      <div className="text-xs text-gray-400">{deposit.email || `ID ${deposit.userId}`}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-sky-300">{deposit.code}</td>
                    <td className="px-4 py-3 text-gray-300">
                      {deposit.createdAt ? new Date(deposit.createdAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={editableAmounts[deposit.id] ?? deposit.amount}
                        onChange={(e) => setEditableAmounts((prev) => ({
                          ...prev,
                          [deposit.id]: e.target.value
                        }))}
                        disabled={Number(deposit.status) === 1}
                        className="w-32 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white disabled:opacity-60"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {Number(deposit.status) === 1 ? (
                        <span className="inline-flex px-3 py-1 rounded-full bg-green-900/40 text-green-300 border border-green-700">
                          Confirmed
                        </span>
                      ) : (
                        <span className="inline-flex px-3 py-1 rounded-full bg-yellow-900/40 text-yellow-300 border border-yellow-700">
                          Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {Number(deposit.status) === 1 ? (
                        <div className="text-xs text-gray-400">
                          Confirmed {deposit.confirmedAt ? new Date(deposit.confirmedAt).toLocaleString() : ''}
                          {deposit.confirmedByUsername ? ` by ${deposit.confirmedByUsername}` : ''}
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleConfirm(deposit)}
                          disabled={confirmingId === deposit.id}
                          className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white font-semibold disabled:opacity-50"
                        >
                          {confirmingId === deposit.id ? 'Confirming...' : 'Mark as seen and credit'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaypalDeposits;
